import { useEffect, useMemo, useState } from 'react'

import { useQuotingEmployeeContext } from 'components/quoting/employees/QuotingEmployeeContext'
import { useQuotePricing } from 'components/quoting/quotes/pricing/QuotePricingProvider'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet'
import { type PlanFiltersState } from 'pages/employer_onboarding/quote/EmployeePlanFilters'
import { PlansTabContent } from 'pages/employer_onboarding/quote/PlansTabContent'
import { Banner } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney, getYearFromDateString } from 'utils'
import { getEmployeePlanParams } from 'utils/quote_calculations'
import {
  useGetQuotingQuoteResultsQuoteIdPlans as useGetPlans,
} from 'utils/web_api'
import { type MemberOnboardingMedicalPlanDisplayItem, type QuotingEmployee, type QuotingQuoteResult } from 'utils/web_api.schemas'

import { useQuote } from './QuoteProvider'

const EmployeeDetailsSheetContent = ({ employee }: { employee: QuotingEmployee }) => {
  const {
    monthlyAllowanceForEmployee,
    isAllowanceAffordable,
    minAffordableAllowanceForEmployee,
    isEmployeeInProjectedState,
  } = useQuotingEmployeeContext()
  const { quoteId } = useQuotePricing()
  const { quote } = useQuote()

  const [filters, setFilters] = useState<PlanFiltersState>({
    metalTiers: [],
    carriers: [],
    planTypes: [],
    onlyFullyCovered: false,
  })

  const monthlyAllowance = employee ? monthlyAllowanceForEmployee(employee) : 0

  const { data: plans, isLoading: plansLoading, error: plansError } = useGetPlans(
    quoteId,
    getEmployeePlanParams(employee, monthlyAllowance),
    {
      query: {
        refetchOnWindowFocus: false,
      },
    }
  )

  const isUnaffordable = useMemo(() => {
    if (!isAllowanceAffordable) return false
    return !isAllowanceAffordable(employee)
  }, [employee, isAllowanceAffordable])

  const minAffordableAllowance = useMemo(() => {
    if (!minAffordableAllowanceForEmployee) return
    return minAffordableAllowanceForEmployee(employee)
  }, [employee, minAffordableAllowanceForEmployee])

  const isProjectedData = useMemo(() => {
    if (!isEmployeeInProjectedState) return false
    return isEmployeeInProjectedState(employee)
  }, [employee, isEmployeeInProjectedState])

  const filteredPlans = useMemo(() => {
    if (!plans || !plans.plan_items) return []

    return plans.plan_items.filter((plan: MemberOnboardingMedicalPlanDisplayItem) => {
      if (filters.metalTiers.length > 0 && !filters.metalTiers.includes(plan.plan.metal_tier)) {
        return false
      }

      if (filters.carriers.length > 0 && !filters.carriers.includes(plan.plan.carrier_name)) {
        return false
      }

      if (filters.planTypes.length > 0 && !filters.planTypes.includes(plan.plan.plan_type)) {
        return false
      }

      if (filters.onlyFullyCovered) {
        const planPremium = convertGrapeMoneyToNumber(plan.total_premium)
        if (planPremium > monthlyAllowance) {
          return false
        }
      }

      return true
    })
  }, [plans, filters, monthlyAllowance])

  return (
    <>
      {isUnaffordable && minAffordableAllowance && (
        <div className='mt-2'>
          <Banner type='critical' title='Allowance is unaffordable for this employee.'>
            <p>Minimum affordable allowance for this employee is {formatMoney(minAffordableAllowance)}</p>
          </Banner>
        </div>
      )}
      <PlansTabContent
        employee={employee as QuotingQuoteResult}
        plans={plans?.plan_items}
        plansLoading={plansLoading}
        hasPlansError={Boolean(plansError)}
        filters={filters}
        setFilters={setFilters}
        filteredPlans={filteredPlans}
        monthlyAllowance={monthlyAllowance}
        usingProjectedData={isProjectedData}
        quoteYear={quote?.coverage_start_date ? getYearFromDateString(quote.coverage_start_date) : null}
      />
    </>
  )
}

export default function EmployeeDetailsSheet () {
  const [displayedEmployee, setDisplayedEmployee] = useState<QuotingEmployee>()
  const { selectedEmployee, onEmployeeSelected } = useQuotingEmployeeContext()

  useEffect(() => {
    if (selectedEmployee) {
      setDisplayedEmployee(selectedEmployee)
    } else {
      // Wait for the animation to complete before removing the content
      const timer = setTimeout(() => {
        setDisplayedEmployee(undefined)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [selectedEmployee])

  return (
    <Sheet open={!!selectedEmployee} onOpenChange={() => onEmployeeSelected()}>
      <SheetContent className='xs:max-w-5/6 xs:w-5/6 md:max-w-1/2 md:w-1/2'>
        {displayedEmployee && (
          <>
            <SheetHeader>
              <SheetTitle>{displayedEmployee.name}</SheetTitle>
            </SheetHeader>
            <div className='flex-1 flex flex-col gap-6 mt-4'>
              <EmployeeDetailsSheetContent employee={displayedEmployee} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
